import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { getProductScope } from '@discover-frontend/utils';

import {
  buildStanleyMembershipBody,
  StanleyMembershipTargetContext,
} from './buildStanleyMembershipBody';
import { STANLEY_QUERY_KEYS } from '../../constants';
import { fetchStanleyQueryMembershipSize } from '../../request';
import { StanleyQuerySuggestion } from '../../types';

export const useStanleyQueryMembershipSize = (
  suggestion: StanleyQuerySuggestion
) => {
  const dataSource = useSelector((state) => state.queryBuilder.dataSource);
  const target_selected = useSelector(
    (state) => state.queryBuilder.target_selected
  );
  const target = useSelector((state) => {
    if (!target_selected || !state.queryBuilder?.targets) {
      return null;
    }
    return state.queryBuilder.targets[target_selected] as
      | StanleyMembershipTargetContext
      | undefined;
  });

  const membershipBody = useMemo(() => {
    if (!target || !dataSource || !suggestion?.query_json?.target_query)
      return null;

    try {
      return buildStanleyMembershipBody(dataSource, target, suggestion);
    } catch {
      return null;
    }
  }, [dataSource, target, suggestion]);

  const scope = getProductScope();

  const queryKey = useMemo(
    () => [
      STANLEY_QUERY_KEYS.GET_MEMBERSHIP_SIZE,
      scope,
      membershipBody ? JSON.stringify(membershipBody) : null,
    ],
    [scope, membershipBody]
  );

  return useQuery(
    queryKey,
    () =>
      fetchStanleyQueryMembershipSize(
        membershipBody as Record<string, unknown>
      ),
    {
      enabled: Boolean(membershipBody?.target_query),
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      cacheTime: 1000 * 60 * 60,
    }
  );
};
